import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Authenticated thumbnail loader for protected `/api/files/{path}` URLs.
 * The plain `<img src>` won't carry the Bearer JWT, so we fetch the file
 * as a blob (axios attaches Authorization via the api client) then turn
 * it into an object URL.
 *
 * Reuses the same pattern from `MatchDetail.jsx::Screenshot` so disputes,
 * player-reports, and any future moderator surfaces stay consistent.
 *
 * Props:
 *   path     — server-side storage path (no leading "/api/files/")
 *   alt      — accessibility text
 *   className— optional Tailwind classes (defaults to a small thumbnail)
 *   testId   — data-testid for the rendered <a>
 */
export default function ProtectedImage({ path, alt = "", className = "", testId }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let alive = true;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        createdUrl = URL.createObjectURL(resp.data);
        if (alive) setUrl(createdUrl);
      } catch { /* swallow — viewer just sees the fallback */ }
    })();
    return () => {
      alive = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path]);

  if (!path) return null;
  if (!url) {
    return (
      <div className="mt-2 px-2 py-1 border border-[#E4E4E7] dark:border-[#2A2A30] font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A] inline-block">
        Loading evidence…
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="mt-2 inline-block border border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED] transition-colors"
      title="Open evidence full-size in new tab"
    >
      <img src={url} alt={alt} className={className || "max-h-32 w-auto block"} loading="lazy" />
    </a>
  );
}
